import React, { FC } from 'react';

import { StoreSettingsValues } from 'api/useStoreSettings';
import { EAssetType } from 'constants/enums';
import { useFormikContext } from 'formik';

import { Label } from 'components/ui/label';
import UploadImage from 'components/UploadImage/UploadImage';

import { defaultStoreTheme } from '../../StoreTheme/defaults';

import { FormField } from './FormField';
import { SeoKeywordsField } from './SeoKeywordsField';

type SEOFormValues = NonNullable<StoreSettingsValues['seo']>;

export const SEOSectionFields: FC = () => {
  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<SEOFormValues>();
  const seo = { ...defaultStoreTheme.seo, ...values };

  return (
    <div className="mt-1 grid grid-cols-1 gap-9 md:grid-cols-2 md:col-span-2 [&_input:not([aria-invalid])]:border-zinc-200 [&_input]:placeholder:text-base-mutedForeground">
      <FormField
        label="SEO Title"
        name="title"
        value={seo.title || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        required
        error={touched.title && Boolean(errors.title)}
        helperText={
          (touched.title && errors.title?.toString()) ||
          `${(seo.title || '').length}/60 characters`
        }
        placeholder="Add a title"
      />
      <FormField
        label="Description"
        name="description"
        value={seo.description || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        required
        error={
          touched.description && Boolean(errors.description)
        }
        helperText={
          (touched.description && errors.description?.toString()) ||
          `${(seo.description || '').length}/160 characters`
        }
        placeholder="Write a short summary that describes this page for search engines."
      />
      <div className="space-y-2">
        <UploadImage
          title="SEO / Social Share Image"
          description="JPG, PNG — recommended size 1200×630px"
          buttonText="Upload"
          onUpload={(url: string) => {
            setFieldValue('socialShareImageUrl', url);
          }}
          selectedImageUrl={seo.socialShareImageUrl || ''}
          forceType={EAssetType.SEO}
          variant="storeSettings"
          className="w-full"
          data-testid="seoSocialShareImage"
          buttonDataTestId="uploadSeoSocialShareImage"
        />
      </div>
      <SeoKeywordsField
        keywords={seo.keywords || []}
        onChange={(keywords) => {
          setFieldValue('keywords', keywords);
        }}
      />
      <div className="md:col-span-2 border-t border-base-border pt-9">
        <Label className="mb-9 block font-sans text-lg font-semibold leading-4 text-base-secondaryForeground">
          Social Metadata Override
        </Label>
        <div className="grid grid-cols-1 gap-9 md:grid-cols-2">
          <FormField
            label="Social Title"
            name="socialTitle"
            value={seo.socialTitle || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(errors.socialTitle)}
            helperText={
              errors.socialTitle?.toString() ||
              `${(seo.socialTitle || '').length}/60 characters`
            }
            placeholder="If not set, SEO Title will be used"
          />
          <FormField
            label="Social Description"
            name="socialDescription"
            value={seo.socialDescription || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(errors.socialDescription)}
            helperText={
              errors.socialDescription?.toString() ||
              `${(seo.socialDescription || '').length}/160 characters`
            }
            placeholder="If not set, SEO Description will be used"
          />
        </div>
      </div>
    </div>
  );
};
